import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import CompanyCamPage from './components/CompanyCamPage';
import UserManagement from './components/UserManagement';
import ActivityPage from './components/ActivityPage';
import RemovedPhotosPage from './components/RemovedPhotosPage';
import IntegrationsPage from './components/IntegrationsPage';
import NotificationsPage from './components/NotificationsPage';
import ProfilePage from './components/ProfilePage';
import PublicTimelinePage from './components/PublicTimelinePage';
import AppLayout from './components/AppLayout';
import FloatingCameraButton from './components/FloatingCameraButton';
import CameraCaptureModal from './components/CameraCaptureModal';
import LocationProjectModal from './components/LocationProjectModal';
import { apiUrl } from './config/api';
import { resumePending } from './lib/uploadQueue';

// The client-detail page has its own contextual dual floating button (add + camera), so the
// global one is hidden there to avoid two overlapping floating buttons on desktop.
const GlobalCameraFab = ({ onClick }) => {
  const location = useLocation();
  if (location.pathname.startsWith('/projects/client/')) return null;
  return <FloatingCameraButton onClick={onClick} />;
};

/** /companycam/client/123 -> /projects/client/123, keeping the id. */
const LegacyClientRedirect = () => {
  const { clientId } = useParams();
  return <Navigate to={`/projects/client/${clientId}`} replace />;
};

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraModalClient, setCameraModalClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // client is optional — set when the camera is opened from a specific client's own detail
  // page, so CameraCaptureModal can skip straight to a one-tap confirmation for that client.
  const handleOpenCamera = (client) => {
    setCameraModalClient(client || null);
    setShowCameraModal(true);
  };

  const handleCloseCamera = () => {
    setShowCameraModal(false);
    setCameraModalClient(null);
  };

  const verifyUserStatus = async (token) => {
    try {
      const response = await fetch(apiUrl('/api/auth/verify'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const msg = data.detail || data.message || '';
        if (response.status === 403 || msg.toLowerCase().includes('deactivated')) {
          console.log('User has been deactivated, logging out...');
        } else {
          console.log('Session is no longer valid, logging out...');
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        return false;
      }

      // The server's copy of the user is authoritative — refresh role flags (isAdmin,
      // isSuperAdmin) so changes apply without signing out and back in.
      const data = await response.json().catch(() => ({}));
      return data.user || true;
    } catch (error) {
      console.error('Error verifying user status:', error);
      return false;
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (storedUser && token) {
      try {
        const parsed = JSON.parse(storedUser);
        // Verify the token is still valid before mounting the authenticated app.
        // We await this so we never briefly render the authenticated shell with
        // a stale/expired token (which triggers a cascade of 401s from every
        // component that fires a fetch on mount).
        verifyUserStatus(token).then((valid) => {
          if (valid) {
            const fresh = typeof valid === 'object' ? { ...parsed, ...valid } : parsed;
            localStorage.setItem('user', JSON.stringify(fresh));
            setUser(fresh);
            // Photos from a previous session that never reached the server — the
            // app was killed, the battery died, the signal dropped. Resumed only
            // after the token is known good, so they can't fail on a 401.
            resumePending(token).then((n) => {
              if (n) console.info(`[uploads] resuming ${n} photo(s) from a previous session`);
            });
          }
          // If invalid, verifyUserStatus already cleared localStorage + called setUser(null)
          setIsLoading(false);
        });
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  // Fetch clients when user logs in
  useEffect(() => {
    if (user) {
      fetchClients();
    }
  }, [user]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/api/scraper/clients'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setClients([]);
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
    // Keep userEmail + pinSet in localStorage so the PIN screen shows on next visit
    // (the PIN itself is never stored client-side — only the flag that one exists).
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  if (isLoading) {
    return <div className="loading">Loading</div>;
  }

  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Public — reachable whether logged in or not, no auth required */}
          <Route path="/timeline/:token" element={<PublicTimelinePage />} />

          {!user ? (
            <>
              <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </>
          ) : (
            <>
              <Route
                path="/"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <Dashboard user={user} onOpenCamera={handleOpenCamera} />
                  </AppLayout>
                }
              />
              <Route
                path="/projects"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <CompanyCamPage user={user} onOpenCamera={handleOpenCamera} />
                  </AppLayout>
                }
              />
              <Route
                path="/projects/client/:clientId"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <CompanyCamPage user={user} onOpenCamera={handleOpenCamera} />
                  </AppLayout>
                }
              />
              {/* The page used to live at /companycam. Project links were shared from there
                  and installed PWAs cache their own start URLs, so the old paths keep
                  working and simply forward to the new ones. */}
              <Route path="/companycam" element={<Navigate to="/projects" replace />} />
              <Route path="/companycam/client/:clientId" element={<LegacyClientRedirect />} />
              <Route
                path="/users"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <UserManagement user={user} />
                  </AppLayout>
                }
              />
              <Route
                path="/activity"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <ActivityPage user={user} />
                  </AppLayout>
                }
              />
              <Route
                path="/removed-photos"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <RemovedPhotosPage user={user} />
                  </AppLayout>
                }
              />
              <Route
                path="/integrations"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <IntegrationsPage user={user} />
                  </AppLayout>
                }
              />
              <Route
                path="/notifications"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <NotificationsPage />
                  </AppLayout>
                }
              />
              <Route
                path="/profile"
                element={
                  <AppLayout user={user} onLogout={handleLogout} onOpenCamera={handleOpenCamera}>
                    <ProfilePage user={user} onLogout={handleLogout} />
                  </AppLayout>
                }
              />
              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}
        </Routes>

        {user && (
          <>
            {/* Opened with no client context, so — unlike the per-project camera button, which
                already knows who it's shooting for and confirms directly — this goes through the
                location-finding flow first: match an existing project by GPS, else offer a GHL
                match, else create one, before ever opening the camera. */}
            <GlobalCameraFab onClick={() => setShowLocationModal(true)} />
            <LocationProjectModal
              isOpen={showLocationModal}
              onClose={() => setShowLocationModal(false)}
              intent="camera"
              clients={clients}
              token={localStorage.getItem('token')}
              onResolved={(client) => handleOpenCamera(client)}
            />
            <CameraCaptureModal
              isOpen={showCameraModal}
              onClose={handleCloseCamera}
              clients={clients}
              user={user}
              preselectedClient={cameraModalClient}
            />
          </>
        )}
      </Router>
    </div>
  );
}

export default App;
